/**
 * Document API Service
 *
 * API calls for document/invoice listing and management.
 * Copied from invoice-management, uses B2C api.ts.
 */

import { apiService, ApiResponse } from './api';

// =============================================================================
// Types
// =============================================================================

export interface DocumentItem {
    id: string;
    company_id: string;
    user_id: string;
    type: 'invoice' | 'quote' | 'income' | 'bank-statement';
    filename: string;
    file_size: number;
    ocr_status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    extraction_status?: 'pending' | 'completed' | 'failed';
    extraction_error?: string;
    extracted_text?: string;
    extracted_data?: {
        line_items?: any[];
        total_amount?: number;
        invoice_number?: string;
        invoice_date?: string;
        supplier_name?: string;
        vendor?: {
            name?: string;
            [key: string]: any;
        };
        financials?: {
            total_amount?: number;
            currency?: string;
            tax?: number;
            subtotal?: number;
        };
        [key: string]: any;
    };
    comparison?: {
        linked_quote_id?: string;
        status?: string;
        match_score?: number;
    };
    ocr_error?: string;
    created_at: string;
    updated_at: string;
    deleted_at?: string;
    duplicate_of?: string;
    duplicate_reason?: string;
    is_primary_duplicate?: boolean;
    file_hash?: string;
    duplicate_seen?: boolean;
}

export interface DocumentListResponse {
    documents: DocumentItem[];
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_prev: boolean;
    match_summary?: { matched: number; unmatched: number };
}

export interface BankTransactionRow {
    id: string;
    statement_id: string;
    tx_index: number;
    filename: string;
    date: string;
    description: string;
    amount: number;
    type: 'debit' | 'credit';
    balance: number | null;
    currency: string;
    is_duplicate: boolean;
    is_excluded: boolean;
    matched_doc: string | null;
}

export interface TransactionListResponse {
    transactions: BankTransactionRow[];
    total: number;
    page: number;
    page_size: number;
    has_next: boolean;
    has_prev: boolean;
}

// =============================================================================
// API Functions
// =============================================================================

export interface DocumentFilters {
    search?: string;
    filter_date?: string;
    filter_amount?: string;
    filter_currency?: string;
    filter_supplier?: string;
    filter_category?: string;
    filter_filename?: string;
    filter_invoice_number?: string;
    filter_bank_name?: string;
    filter_account_number?: string;
    filter_period_start?: string;
    filter_period_end?: string;
    sort_by?: string;
    sort_order?: string;
}

export const getDocuments = async (
    page: number = 1,
    pageSize: number = 20,
    type?: 'invoice' | 'quote' | 'income' | 'bank-statement',
    matchStatus?: 'all' | 'matched' | 'unmatched',
    filters?: DocumentFilters,
): Promise<ApiResponse<DocumentListResponse>> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (type) params.type = type;
    if (matchStatus) params.match_status = matchStatus;
    if (filters) {
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    }
    return apiService.get<DocumentListResponse>('/api/documents', params);
};

export const createManualDocument = async (
    type: 'invoice' | 'income' | 'bank-statement',
    extractedData: Record<string, any>,
    filename?: string,
): Promise<ApiResponse<DocumentItem>> => {
    const body: Record<string, any> = { type, extracted_data: extractedData };
    if (filename) body.filename = filename;
    return apiService.post<DocumentItem>('/api/documents/create', body);
};

export const getDocument = async (
    documentId: string
): Promise<ApiResponse<DocumentItem>> => {
    return apiService.get<DocumentItem>(`/api/documents/${documentId}`);
};

export const deleteDocument = async (
    documentId: string,
    source?: string
): Promise<ApiResponse<void>> => {
    const query = source ? `?source=${encodeURIComponent(source)}` : '';
    return apiService.delete<void>(`/api/documents/${documentId}${query}`);
};

export const updateDocumentFields = async (
    documentId: string,
    fields: Record<string, any>
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(
        `/api/documents/${documentId}/fields`,
        { fields }
    );
};

export const reprocessPending = async (): Promise<ApiResponse<{ reprocessed: number }>> => {
    return apiService.post<{ reprocessed: number }>('/api/documents/reprocess-pending');
};

export const getDeletedDocuments = async (
    page: number = 1,
    pageSize: number = 20,
    type?: string
): Promise<ApiResponse<DocumentListResponse>> => {
    const params: Record<string, any> = { page, page_size: pageSize };
    if (type) params.type = type;
    return apiService.get<DocumentListResponse>('/api/documents/trash', params);
};

export const restoreDocument = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/api/documents/${documentId}/restore`);
};

export const permanentDeleteDocuments = async (
    documentIds: string[],
    source?: string
): Promise<ApiResponse<{ count: number }>> => {
    return apiService.post<{ count: number }>('/api/documents/trash/permanent-delete', { document_ids: documentIds, source: source || 'trash' });
};

export const cancelProcessing = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/api/documents/${documentId}/cancel-processing`);
};

export const retryDocument = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.post<void>(`/api/documents/${documentId}/retry`);
};

export const swapDocumentDates = async (
    documentId: string
): Promise<ApiResponse<DocumentItem>> => {
    return apiService.post<DocumentItem>(`/api/documents/${documentId}/swap-dates`);
};

export const markDocumentDuplicate = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(`/api/documents/${documentId}/mark-duplicate`);
};

export const unmarkDocumentDuplicate = async (
    documentId: string
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(`/api/documents/${documentId}/unmark-duplicate`);
};

export const updateTransactions = async (
    documentId: string,
    transactions: any[],
    currency?: string
): Promise<ApiResponse<DocumentItem>> => {
    const body: Record<string, any> = { transactions };
    if (currency) body.currency = currency;
    return apiService.patch<DocumentItem>(
        `/api/documents/${documentId}/transactions`,
        body
    );
};

// =============================================================================
// Expense Category API
// =============================================================================

export interface ExpenseCategory {
    key: string;
    labels: { tr: string; en: string; de: string };
    description: string;
}

export interface CategoriesResponse {
    categories: ExpenseCategory[];
    is_default: boolean;
}

export const getExpenseCategories = async (): Promise<ApiResponse<CategoriesResponse>> => {
    return apiService.get<CategoriesResponse>('/api/categories');
};

export const updateExpenseCategories = async (
    categories: ExpenseCategory[]
): Promise<ApiResponse<ExpenseCategory[]>> => {
    return apiService.put<ExpenseCategory[]>('/api/categories', { categories });
};

export const resetExpenseCategories = async (): Promise<ApiResponse<CategoriesResponse>> => {
    return apiService.post<CategoriesResponse>('/api/categories/reset');
};

// =============================================================================
// DATEV Settings API
// =============================================================================

export interface DatevSettings {
    beraternummer: string;
    mandantennummer: string;
    wj_beginn_monat: number;
    sachkontenlaenge: number;
    gegenkonto: string;
    skr_type: string;
}

export interface DatevSettingsResponse {
    settings: DatevSettings;
    is_default: boolean;
}

export const getDatevSettings = async (): Promise<ApiResponse<DatevSettingsResponse>> => {
    return apiService.get<DatevSettingsResponse>('/api/datev');
};

export const updateDatevSettings = async (
    settings: DatevSettings
): Promise<ApiResponse<DatevSettings>> => {
    return apiService.put<DatevSettings>('/api/datev', settings);
};

// =============================================================================
// DATEV SKR Chart of Accounts API (Kontenrahmen)
// =============================================================================

export type SkrType = 'SKR03' | 'SKR04';

export interface SkrAccount {
    konto: string;
    label: string;
}

export interface SkrChartResponse {
    skr_type: SkrType;
    sachkontenlaenge: number;
    is_default: boolean;
    charts: {
        SKR03: SkrAccount[];
        SKR04: SkrAccount[];
    };
}

export const getSkrChart = async (): Promise<ApiResponse<SkrChartResponse>> => {
    return apiService.get<SkrChartResponse>('/api/datev/skr-chart');
};

export const updateSkrChart = async (
    skr_type: SkrType,
    chart: SkrAccount[],
): Promise<ApiResponse<SkrChartResponse>> => {
    return apiService.put<SkrChartResponse>('/api/datev/skr-chart', {
        skr_type,
        chart,
    });
};

export const resetSkrChart = async (): Promise<ApiResponse<SkrChartResponse>> => {
    return apiService.delete<SkrChartResponse>('/api/datev/skr-chart');
};

// =============================================================================
// Duplicate / Exclude / Delete Transaction API
// =============================================================================

export const updateDuplicatePrimary = async (
    documentId: string,
    txIndex: number,
    isPrimary: boolean,
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(
        `/api/documents/${documentId}/duplicates/primary`,
        { tx_index: txIndex, is_primary: isPrimary },
    );
};

export const excludeTransaction = async (
    documentId: string,
    txIndex: number,
    excluded: boolean,
    reason: string = 'manual_duplicate',
    force: boolean = false,
): Promise<ApiResponse<{ has_existing_match?: boolean }>> => {
    return apiService.patch<{ has_existing_match?: boolean }>(
        `/api/documents/${documentId}/transactions/${txIndex}/exclude`,
        { excluded, reason, force },
    );
};

export const deleteTransaction = async (
    documentId: string,
    txIndex: number,
): Promise<ApiResponse<void>> => {
    return apiService.delete<void>(
        `/api/documents/${documentId}/transactions/${txIndex}`,
    );
};

export const restoreTransaction = async (
    documentId: string,
    txIndex: number,
): Promise<ApiResponse<void>> => {
    return apiService.patch<void>(
        `/api/documents/${documentId}/transactions/${txIndex}/restore`,
    );
};

export const getAllBankTransactions = async (
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    sortBy?: string,
    sortOrder?: string,
    filters?: Record<string, string>,
    statementIds?: string[],
): Promise<ApiResponse<TransactionListResponse>> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (search) params.search = search;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    if (statementIds?.length) params.statement_ids = statementIds.join(',');
    if (filters) {
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    }
    return apiService.get<TransactionListResponse>('/api/documents/transactions', params);
};

const documentApi = {
    getDocuments,
    createManualDocument,
    getDocument,
    deleteDocument,
    updateDocumentFields,
    updateTransactions,
    reprocessPending,
    getDeletedDocuments,
    restoreDocument,
    permanentDeleteDocuments,
    cancelProcessing,
    retryDocument,
    swapDocumentDates,
    markDocumentDuplicate,
    unmarkDocumentDuplicate,
    getExpenseCategories,
    updateExpenseCategories,
    resetExpenseCategories,
    updateDuplicatePrimary,
    excludeTransaction,
    deleteTransaction,
    restoreTransaction,
    getAllBankTransactions,
    getDatevSettings,
    updateDatevSettings,
    getSkrChart,
    updateSkrChart,
    resetSkrChart,
};

export default documentApi;
